import { Link } from "react-router-dom";
import { facts, leadSlug, getArticle, categories, categoryName, verdictMeta } from "@/mocks/articles";
import { ArticleCard, VerdictBadge } from "@/components/base/ArticleCard";
import Breadcrumb, { Footer } from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import useSeo from "@/hooks/useSeo";

export default function Home() {
  useSeo(
    "辨真社 · 免费爆料真实性核查与辟谣可信度分析",
    "辨真社专注免费爆料真实性核查：用证据链梳理实锤、存疑与已辟谣信息，辟谣对比与深度解析，让你吃瓜不被带节奏，看清事件本质。"
  );

  const lead = getArticle(leadSlug) ?? facts[0];
  const gridArticles = facts.filter((a) => a.slug !== lead.slug).slice(0, 5);
  const hotRank = [...facts].sort((a, b) => b.heat - a.heat).slice(0, 5);

  return (
    <>
      <Navbar />
      <main>
        {/* ===== Hero ===== */}
        <section className="relative overflow-hidden bg-foreground-950">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%2C%20moody%20editorial%20background%20with%20abstract%20layers%20of%20magnifying%20glass%20and%20newspaper%20texture%20shadows%20in%20warm%20amber%20and%20ink%20tones%2C%20noise%20and%20grain%2C%20cinematic%20documentary%20investigation%20theme%2C%20no%20text%2C%20no%20faces&width=1800&height=1000&seq=hero-fact-2026&orientation=landscape"
            alt="关于爆料真伪核查的编辑部主题背景"
            title="辨真社 爆料真实性核查"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 via-foreground-950/70 to-foreground-950/90"></div>

          <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-20 text-center md:px-6 md:py-28">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-50/20 bg-background-50/10 px-4 py-1.5 text-xs font-medium tracking-wide text-background-50 backdrop-blur">
              <i className="ri-focus-3-line" />
              免费爆料真实性 · 可信度分析平台
            </span>
            <h1 className="mt-6 max-w-4xl font-heading text-4xl font-black leading-tight text-background-50 md:text-6xl">
              吃瓜之前，先辨真伪
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-background-100/90 md:text-lg">
              面对满天飞的“实锤”与“爆料”，我们用证据链、来源溯源与多方对撞帮你分辨真假。
              辟谣对比、深度解析，只为让你在吃瓜时不被带节奏，真正看清事件本质。
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
              <Link
                to="/topics"
                className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-7 text-base font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览最新核验
                <i className="ri-arrow-right-line ml-2" />
              </Link>
              <Link
                to="/guide"
                className="inline-flex h-12 items-center justify-center whitespace-nowrap rounded-md border border-background-50/30 px-7 text-base font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                学习辨别方法
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-300/40 bg-accent-500/15 px-3 py-1 text-xs text-background-50">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />实锤 · 已证实
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary-300/40 bg-secondary-500/15 px-3 py-1 text-xs text-background-50">
                <span className="h-1.5 w-1.5 rounded-full bg-secondary-300" />存疑 · 待考证
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-300/40 bg-primary-500/15 px-3 py-1 text-xs text-background-50">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-400" />辟谣 · 不实
              </span>
            </div>
          </div>
        </section>

        {/* ===== 头条求证 ===== */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest text-primary-700 uppercase">
                <i className="ri-flashlight-line" />
                今日头条求证
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                最新核验，先看这条
              </h2>
            </div>
            <Breadcrumb items={[{ label: "首页" }]} />
          </div>

          <Link
            to={`/article/${lead.slug}`}
            className="group mt-8 grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:grid-cols-2"
          >
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-background-100 lg:aspect-auto lg:min-h-[360px]">
              <img
                src={lead.cover}
                alt={lead.title}
                title={lead.title}
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute left-4 top-4"><VerdictBadge verdict={lead.verdict} /></div>
            </div>
            <div className="flex flex-col justify-center gap-4 p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {categoryName(lead.category)}
                </span>
                <span>{lead.date}</span>
                <span>约 {lead.readMinutes} 分钟读完</span>
              </div>
              <h3 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
                {lead.title}
              </h3>
              <p className="text-base leading-relaxed text-foreground-700">{lead.claim}</p>
              <p className="border-l-2 border-primary-500 pl-4 text-sm leading-relaxed text-foreground-700">
                <strong className="font-medium text-foreground-900">核验结论：</strong>
                {lead.summary}
              </p>
              <span className="inline-flex w-fit items-center gap-2 rounded-md bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-800 transition-colors group-hover:bg-primary-100">
                查看完整证据链
                <i className="ri-arrow-right-line" />
              </span>
            </div>
          </Link>
        </section>

        {/* ===== 数据带 ===== */}
        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-8 px-4 py-10 md:grid-cols-4 md:px-6">
            {[
              { n: "128", t: "累计核验事件", i: "ri-archive-2-line" },
              { n: "46", t: "已定性为不实", i: "ri-close-circle-line" },
              { n: "31", t: "追证补实为可信", i: "ri-checkbox-circle-line" },
              { n: "90%", t: "结论均有公开来源", i: "ri-community-line" },
            ].map((s) => (
              <div key={s.t} className="flex items-center gap-4 px-2">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${s.i} text-2xl`} />
                </span>
                <div>
                  <div className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                    {s.n}
                  </div>
                  <div className="text-xs text-foreground-600 md:text-sm">{s.t}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== 最新核验 + 热度榜 ===== */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              最新核验
            </h2>
            <Link
              to="/topics"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-700 hover:text-primary-600"
            >
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* 左两列卡片 */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-2">
              {gridArticles.slice(0, 4).map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>

            {/* 右栏：热度榜 */}
            <aside className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-fire-line text-primary-600" />
                吃瓜热度榜
              </h3>
              <p className="mt-1 text-xs text-foreground-600">按全网讨论热度排序</p>
              <ol className="mt-5 space-y-1">
                {hotRank.map((a, i) => (
                  <li key={a.slug}>
                    <Link
                      to={`/article/${a.slug}`}
                      className="group flex items-start gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-background-50"
                    >
                      <span
                        className={`font-heading text-xl font-black leading-none ${
                          i < 3 ? "text-primary-600" : "text-foreground-300"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                          {a.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-xs text-foreground-600">
                          {categoryName(a.category)}
                          <span className="inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-current text-primary-500" />
                            {verdictMeta[a.verdict].label}
                          </span>
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
              <Link
                to="/topics"
                className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-md border border-background-200 bg-background-50 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-secondary-300"
              >
                进入全部事件 <i className="ri-arrow-right-s-line" />
              </Link>
            </aside>
          </div>
        </section>

        {/* ===== 核验方法论（可跳转） ===== */}
        <section className="border-y border-background-200 bg-secondary-100/50">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest text-secondary-800 uppercase">
                <i className="ri-search-eye-line" />
                我们如何求证
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                四条铁律，教你看穿爆料
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground-700">
                网络世界里，情绪跑得永远比事实快。掌握以下四个判断维度，你也能在十几秒内对一条爆料建立初步的“可信度直觉”。
              </p>
              <Link
                to="/guide"
                className="mt-6 inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md bg-foreground-950 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800"
              >
                查看完整方法论 <i className="ri-arrow-right-line ml-2" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { n: "01", t: "先看来源", d: "是谁在发？有没有历史与立场？", i: "ri-user-follow-line" },
                { n: "02", t: "核对证据", d: "截图能否溯源，数据能否复现？", i: "ri-focus-2-line" },
                { n: "03", t: "多方对撞", d: "不同信源是否指向同一事实？", i: "ri-node-tree" },
                { n: "04", t: "动机分析", d: "这个时间点，为什么曝光？", i: "ri-lightbulb-flash-line" },
              ].map((s) => (
                <div key={s.n} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-200 text-secondary-900">
                      <i className={`${s.i} text-xl`} />
                    </span>
                    <span className="font-heading text-3xl font-black text-secondary-300">{s.n}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{s.t}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== 分类专题 ===== */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-end justify-between">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest text-primary-700 uppercase">
                <i className="ri-price-tag-3-line" />
                按领域吃瓜
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                分类核验专题
              </h2>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {categories.map((c, i) => (
              <Link
                key={c.key}
                to={`/topics?c=${c.key}`}
                className="group rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
              >
                <span
                  className={`flex h-11 w-11 items-center justify-center rounded-md text-xl ${
                    i % 2 === 0 ? "bg-accent-100 text-accent-700" : "bg-secondary-100 text-secondary-800"
                  }`}
                >
                  <i className={["ri-fire-line","ri-music-2-line","ri-shopping-bag-3-line","ri-briefcase-4-line","ri-cpu-line"][i]} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-800">
                  {c.name}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">{c.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
                  去查证 <i className="ri-arrow-right-line" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* ===== FAQ 预览 ===== */}
        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                  关于“爆料真假”
                </h2>
                <p className="mt-4 text-base leading-relaxed text-foreground-700">
                  读者最常问的几个问题。如果还有疑问，欢迎查看完整常见问题页。
                </p>
                <Link
                  to="/faq"
                  className="mt-5 inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-6 text-sm font-semibold text-foreground-900 transition-colors hover:border-secondary-300"
                >
                  查看全部问答 <i className="ri-arrow-right-line ml-2" />
                </Link>
              </div>
              <div className="space-y-3 lg:col-span-3">
                {[
                  { q: "看到一张“实锤截图”就一定是真的吗？", a: "不一定。截图可以被修图或 AI 生成，单一截图只能作为线索，需要结合来源、原始画面与多方对撞才能定论。" },
                  { q: "“辟谣”一定是可靠的最终结论吗？", a: "高质量的辟谣应附证据链与公开来源。若官方澄清缺乏证据、力度不足，我们仍会将其标为“待考证”，而非简单盖棺定论。" },
                  { q: "为什么同一事件，不同平台的结论不一样？", a: "平台信息差、剪辑语境与情绪传播都会造成偏差。请以可复现的证据与权威机构发布为准，而不是转发量最高的那条。" },
                ].map((f) => (
                  <div key={f.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <h3 className="font-heading text-base font-bold text-foreground-950">{f.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== 口碑 / 关于 ===== */}
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-foreground-950/60 to-foreground-950/90"></div>
          <div className="relative mx-auto flex max-w-7xl flex-col items-center px-4 py-16 text-center md:px-6 md:py-20">
            <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest text-accent-300 uppercase">
              <i className="ri-quote-text" />
              读者怎么说
            </span>
            <blockquote className="mt-6 max-w-3xl font-heading text-2xl font-bold leading-relaxed text-background-50 md:text-3xl">
              “以前吃瓜全靠转发量判断，现在我会先来这里查一眼结论和证据链，
              被带节奏的次数真的少了很多。”
            </blockquote>
            <p className="mt-4 text-sm text-background-100/80">—— 一位关注网络热点的普通读者</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/about"
                className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md bg-background-50 px-6 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-100"
              >
                关于辨真社
              </Link>
              <Link
                to="/topics"
                className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md border border-background-50/30 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                去查最近的瓜
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}